<script lang="ts">
	import type { CompleteVariant } from '$lib/types';
	import { getTranslate } from '@tolgee/svelte';
	import { classes } from '@thearc-hq/ui-kit/utils';
	import type { CompleteStoryData } from '$lib/storyblok/types';
	import { StoryblokComponent } from '@storyblok/svelte';
	import SafetyDrawer from '$lib/components/Drawer/Product/SafetyDrawer.svelte';
	import SizesDrawer from '$lib/components/Drawer/Product/SizesDrawer.svelte';
	import MultiBrancheDetailDrawer from '$lib/components/Drawer/Product/MultiBrancheDetailDrawer.svelte';
	import { pushState } from '$app/navigation';
	import { page } from '$app/stores';
	import FocusTrap from '$lib/components/Drawer/FocusTrap.svelte';

	type DetailsDrawerCategory =
		| 'technicalData'
		| 'material'
		| 'qAndA'
		| 'size'
		| 'multibranche'
		| 'safety';

	const DetailPages: Array<DetailsDrawerCategory> = [
		'technicalData',
		'material',
		'qAndA',
		'safety'
	];

	const hasDataForClusterType = (
		cluster: DetailsDrawerCategory,
		variant?: CompleteVariant | null
	) => {
		switch (cluster) {
			case 'qAndA':
				return !!variant?.faq_d2c?.value;
			case 'safety':
				return !!variant?.hazard_data?.value;
			case 'technicalData':
				return !!variant?.technical_data?.value;
			case 'material':
				return !!variant?.material_d2c?.value || !!variant?.care_d2c?.value;
			default:
				return true;
		}
	};

	const { t } = getTranslate();

	export let variant: CompleteVariant | null = null;
	export let storyblokData: CompleteStoryData;

	const getStoryData = (drawer: DetailsDrawerCategory) => {
		switch (drawer) {
			case 'material':
				return [
					...(storyblokData?.material?.variant ?? []),
					...(storyblokData?.care?.variant ?? []),
					...(storyblokData?.material?.product ?? []),
					...(storyblokData?.care?.product ?? [])
				];
			case 'qAndA':
				return storyblokData.faq;
			case 'size':
				return storyblokData.measure[0];
			default:
				console.warn('No story data found for drawer', drawer);
				return [];
		}
	};
</script>

<div class="pt-8 pl-0 mb-1 md:pl-4 md:w-2/6">
	<hr />
	<ul class="divide-y divide-gray-200">
		{#each DetailPages as detailPage}
			{@const hasData = hasDataForClusterType(detailPage, variant)}
			{#if hasData}
				<li class="flex">
					<button
						class="flex items-center justify-between flex-1 py-4 cursor-pointer"
						on:click={() =>
							pushState('', {
								informationButton: { category: detailPage }
							})}
					>
						<span class="font-black text-gray-900 text-h-14 hover:text-dark-secondary">
							{$t(`product.${detailPage}`)}
						</span>
						{#if detailPage}
							<div
								class="i-sprenger-chevron-right flex-shrink-0 w-5 h-5 text-accent-primary"
								aria-hidden="true"
							/>
						{/if}
					</button>
				</li>
			{/if}
		{/each}
	</ul>
	<hr />
</div>
{#if $page.state?.informationButton?.category}
	<div
		on:click={() => history.back()}
		class="fixed w-full h-full z-30 left-0 top-0 hidden md:block bg-black bg-opacity-20"
	></div>
{/if}
<div
	class={classes(
		'fixed right-0 top-0 max-w-lg z-60 w-full h-full bg-white transition transform duration-300 overflow-y-auto',
		$page.state?.informationButton?.category ? '' : 'translate-x-full'
	)}
>
	<div class="p-4">
		<div class="flex justify-end">
			<button
				on:click={() => history.back()}
				class="i-sprenger-x-circle w-8 h-8 text-accent-primary"
			></button>
		</div>
		<div class="flex flex-col gap-7 pb-7">
			{#if $page.state?.informationButton?.category}
				<h3>{$t(`product.${$page.state?.informationButton?.category}`)}</h3>
			{/if}

			{#if $page.state?.informationButton?.category === 'material' || $page.state?.informationButton?.category === 'qAndA'}
				{@const storyData = getStoryData($page.state?.informationButton?.category)}
				{#if storyData}
					{#each storyData as story}
						<StoryblokComponent blok={story?.content} />
					{/each}
				{/if}
			{:else if $page.state?.informationButton?.category === 'technicalData'}
				{@const parsedData = JSON.parse(variant?.technical_data?.value ?? '{}')}
				<div class="grid gap-4 gap-x-8 grid-cols-3">
					{#each Object.entries(parsedData) as [key, value]}
						<div class="text-h-14 shrink">
							{key}
						</div>
						<div class="text-p-14 col-span-2">
							{@html value.split('§')[0]}
						</div>
					{/each}
				</div>
			{:else if $page.state?.informationButton?.category === 'safety'}
				{@const safetyData = JSON.parse(variant?.hazard_data?.value ?? '{}')}
				<SafetyDrawer {safetyData} sku={variant?.sku?.value || ''} />
			{:else if $page.state?.informationButton?.category === 'multibranche'}
				<MultiBrancheDetailDrawer />
			{:else if $page.state?.informationButton?.category === 'size'}
				{@const storyData = getStoryData($page.state?.informationButton?.category)}
				{#if storyData}
					<SizesDrawer sizeData={storyData?.content} />
				{/if}
			{/if}
		</div>
	</div>
</div>
